import { useEffect, useState } from 'react';
import { Modal } from '../../../shared/components/Modal';
import Button from '../../../shared/components/Button';
import { Input } from '../../../shared/components/Input';
import { TextArea } from '../../../shared/components/TextArea';
import type {
  PackageTemplate,
  CreatePackageTemplatePayload,
} from '../api/packagesApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  editing: PackageTemplate | null;
  onSubmit: (values: CreatePackageTemplatePayload) => Promise<void> | void;
  isSaving: boolean;
}

export function PackageTemplateModal({ isOpen, onClose, editing, onSubmit, isSaving }: Props) {
  const [name, setName] = useState('');
  const [priceNaira, setPriceNaira] = useState('');
  const [stepsText, setStepsText] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setName(editing?.name ?? '');
    setPriceNaira(editing ? String((editing.price ?? 0) / 100) : '');
    setStepsText((editing?.steps ?? []).join('\n'));
    setDescription(editing?.description ?? '');
  }, [isOpen, editing]);

  const steps = stepsText.split('\n').map((s) => s.trim()).filter(Boolean);
  const priceKobo = Math.round((parseFloat(priceNaira) || 0) * 100);
  const canSubmit = name.trim().length > 0 && steps.length > 0 && priceKobo >= 0 && !isSaving;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    await onSubmit({
      name: name.trim(),
      steps,
      price: priceKobo,
      description: description.trim() || undefined,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-foreground">
            {editing ? 'Edit package template' : 'New package template'}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-2xl leading-none" aria-label="Close">
            &times;
          </button>
        </div>

        <div className="space-y-5">
          <Input label="Name" placeholder="e.g. Bridal Glam Package" value={name} onChange={(e) => setName(e.target.value)} />
          <Input label="Price (₦)" type="number" min={0} step="0.01" placeholder="0.00" value={priceNaira} onChange={(e) => setPriceNaira(e.target.value)} />
          <TextArea
            label="Steps (one per line)"
            rows={5}
            placeholder={'Consultation\nTrial\nWedding Day'}
            value={stepsText}
            onChange={(e) => setStepsText(e.target.value)}
          />
          <TextArea
            label="Description (optional)"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="flex justify-end gap-3 mt-8">
          <Button variant="outline" size="md" type="button" onClick={onClose} disabled={isSaving}>Cancel</Button>
          <Button size="md" type="button" onClick={handleSubmit} loading={isSaving} disabled={!canSubmit}>
            {editing ? 'Save template' : 'Create template'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
